import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Segment } from 'tea-component';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import type {
  IAllPlatformSegmentOption,
  IAllPlatformTabConfig,
  IKpiEventAllPlatformTabTrendServiceProps,
} from './_type';
import './index.scss';

const DEFAULT_SEGMENT_OPTIONS: IAllPlatformSegmentOption[] = [
  { value: 'all', textCn: 'All', textEn: 'All' },
  { value: 'platform', textCn: '分平台', textEn: 'Platform' },
];

/**
 * All / Platform 切换：子节点须按顺序传两个分支——
 * [0] KpiEventMetricTrendService（汇总曲线）
 * [1] KpiEventDimensionTrendService（维度拆分，默认 system）
 * 参考 Applovin D7：Segment 注入下游 EventMetricTrend.contentHeader。
 */
const KpiEventAllPlatformTabTrendService = (
  props: IKpiEventAllPlatformTabTrendServiceProps,
) => {
  const {
    style,
    className,
    config,
    children,
    allPlatformTabConfig: tabProp,
    ...passThroughToTrendService
  } = props;

  const tabCfg =
    tabProp ??
    (config?.allPlatformTabConfig as IAllPlatformTabConfig | undefined);

  const { isEN } = useLocal();

  const segmentVisible = tabCfg?.segmentVisible !== false;

  const segmentOptions = useMemo(() => {
    const raw = tabCfg?.segmentOptions;
    if (Array.isArray(raw) && raw.length >= 2) {
      return raw;
    }
    return DEFAULT_SEGMENT_OPTIONS;
  }, [tabCfg?.segmentOptions]);

  const defaultTabValue = useMemo(() => {
    const configured = tabCfg?.defaultTabValue;
    if (
      configured &&
      segmentOptions.some(o => String(o.value) === String(configured))
    ) {
      return String(configured);
    }
    return String(segmentOptions[0]?.value ?? 'all');
  }, [tabCfg?.defaultTabValue, segmentOptions]);

  const [tabValue, setTabValue] = useState<string>(defaultTabValue);

  useEffect(() => {
    setTabValue(defaultTabValue);
  }, [defaultTabValue]);

  const segmentTeaOptions = useMemo(
    () =>
      segmentOptions.map(o => ({
        value: String(o.value),
        text: isEN ? o.textEn : o.textCn,
      })),
    [isEN, segmentOptions],
  );

  const segmentNode =
    segmentVisible && segmentOptions.length > 0 ? (
      <div
        className={
          tabCfg?.segmentClassName ||
          'dbd-kpi-all-platform-tab-trend-service__segment'
        }
      >
        <Segment
          value={tabValue}
          onChange={(v: string) => setTabValue(String(v))}
          options={segmentTeaOptions}
        />
      </div>
    ) : null;

  const childList = React.Children.toArray(children);
  const allBranch = childList[0];
  const platformBranch = childList[1];

  const isPlatformTab = String(tabValue) === 'platform';
  const activeChild =
    isPlatformTab && platformBranch !== undefined ? platformBranch : allBranch;

  if (!React.isValidElement(activeChild)) {
    return <>{activeChild}</>;
  }

  const headerProps: Record<string, unknown> = {
    ...passThroughToTrendService,
    contentHeader: segmentNode,
  };

  return (
    <div
      className={classNames(
        'dbd-kpi-all-platform-tab-trend-service',
        className,
      )}
      style={style}
    >
      {React.cloneElement(activeChild, headerProps)}
    </div>
  );
};

export default KpiEventAllPlatformTabTrendService;

export type {
  IAllPlatformSegmentOption,
  IAllPlatformTabConfig,
  IKpiEventAllPlatformTabTrendServiceProps,
} from './_type';
